import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { checkIfSectionIsSubmodel } from '../../../services/CategoryHelper';
import { SortableCategory } from './SortableCategory';

import type { Dispatch, SetStateAction } from 'react';
import type { ISection } from '../../../../types';

interface ICategoryTreeProps {
  editDataView: boolean;
  expanded: string[];
  handleAddSection: (parent?: ISection | null) => void;
  handleDeleteSection: (section: ISection) => void;
  sections: ISection[];
  selectedSectionId: string | null;
  setExpanded: Dispatch<SetStateAction<string[]>>;
  setSelectedSectionId: Dispatch<SetStateAction<string | null>>;
}

export const CategoryTree: React.FC<ICategoryTreeProps> = ({
  editDataView,
  expanded,
  handleAddSection,
  handleDeleteSection,
  sections,
  selectedSectionId,
  setExpanded,
  setSelectedSectionId,
}) => {
  const renderDroppableTree = (
    nodes: ISection[],
    selectedId: string | null,
    parentExpanded: boolean = false
  ): React.ReactNode =>
    nodes.map((node) => {
      const hasSubcategories = node.sections && node.sections.length > 0;
      const isExpanded = expanded.includes(node.code);
      const highlightChildren = isExpanded && node.code === selectedId;

      if (checkIfSectionIsSubmodel(node)) {
        return null;
      }

      return (
        <TreeItem
          key={node.code}
          itemId={node.code}
          label={
            <div>
              <SortableCategory
                section={node}
                isSelected={node.code === selectedId}
                selectedSectionId={selectedId ?? undefined}
                onSelect={(sectionId: string) => setSelectedSectionId(sectionId)}
                onAddSection={handleAddSection}
                onDeleteSection={handleDeleteSection}
                isParentSelected={parentExpanded}
                editView={editDataView}
              />
            </div>
          }
        >
          {hasSubcategories && node.sections
            ? renderDroppableTree(node.sections, selectedId, highlightChildren)
            : null}
        </TreeItem>
      );
    });

  return (
    <SimpleTreeView
      selectedItems={selectedSectionId}
      expandedItems={expanded}
      onExpandedItemsChange={(_event, nodeIds) => setExpanded(nodeIds)}
      onSelectedItemsChange={(_event, selectedIds) => {
        const id = Array.isArray(selectedIds) ? selectedIds[0] : selectedIds;
        setSelectedSectionId(id);
        if (id && expanded.includes(id)) {
          setExpanded(expanded.filter((expandedId) => expandedId !== id));
        } else if (id) {
          setExpanded([...expanded, id]);
        }
      }}
    >
      {renderDroppableTree(sections, selectedSectionId)}
    </SimpleTreeView>
  );
};
